import { useState, useEffect } from "react";
import axios from "axios";
import { Pencil, Check, X, RotateCcw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Collapsible per-dish name & description editor. Sits under the PriceEditor
 * in the admin Fotos tab. When collapsed only a pencil button is visible; when
 * expanded, textarea fields for ES/EN name and description are shown. Empty
 * string clears the field (reverts to the hard-coded default).
 */
export const TextEditor = ({ itemId, item, adminKey, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [nameEs, setNameEs] = useState(item.name_es || "");
  const [descEs, setDescEs] = useState(item.desc_es || "");
  const [nameEn, setNameEn] = useState(item.name_en || "");
  const [descEn, setDescEn] = useState(item.desc_en || "");
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    setNameEs(item.name_es || "");
    setDescEs(item.desc_es || "");
    setNameEn(item.name_en || "");
    setDescEn(item.desc_en || "");
  }, [item.name_es, item.desc_es, item.name_en, item.desc_en]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/menu/${itemId}/text`, {
        name_es: nameEs,
        desc_es: descEs,
        name_en: nameEn,
        desc_en: descEn,
      }, { params: { key: adminKey } });
      setOk(true);
      setTimeout(() => setOk(false), 1500);
      onChanged && onChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("¿Restablecer nombre y descripción al valor original?")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/menu/${itemId}/text`, { params: { key: adminKey } });
      onChanged && onChanged();
      setOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`text-edit-open-${itemId}`}
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:text-gold border border-q5 hover:border-gold py-1.5 transition-colors"
      >
        <Pencil size={10} /> Editar nombre / descripción
      </button>
    );
  }

  return (
    <div
      data-testid={`text-editor-${itemId}`}
      className="border border-gold/40 bg-[#141414] p-2 space-y-1.5"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-[0.2em] text-gold">Textos</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Cerrar"
          className="text-neutral-500 hover:text-white"
        >
          <X size={12} />
        </button>
      </div>
      <input
        type="text"
        data-testid={`text-name-es-${itemId}`}
        value={nameEs}
        onChange={(e) => setNameEs(e.target.value)}
        placeholder="Nombre (ES)"
        className="w-full bg-[#0A0A0A] border border-q5 text-white text-xs px-2 py-1.5 focus:outline-none focus:border-gold"
      />
      <textarea
        data-testid={`text-desc-es-${itemId}`}
        value={descEs}
        onChange={(e) => setDescEs(e.target.value)}
        placeholder="Descripción (ES)"
        rows={2}
        className="w-full bg-[#0A0A0A] border border-q5 text-white text-xs px-2 py-1.5 focus:outline-none focus:border-gold resize-none"
      />
      <input
        type="text"
        data-testid={`text-name-en-${itemId}`}
        value={nameEn}
        onChange={(e) => setNameEn(e.target.value)}
        placeholder="Name (EN)"
        className="w-full bg-[#0A0A0A] border border-q5 text-neutral-400 text-xs px-2 py-1.5 focus:outline-none focus:border-gold"
      />
      <textarea
        data-testid={`text-desc-en-${itemId}`}
        value={descEn}
        onChange={(e) => setDescEn(e.target.value)}
        placeholder="Description (EN)"
        rows={2}
        className="w-full bg-[#0A0A0A] border border-q5 text-neutral-400 text-xs px-2 py-1.5 focus:outline-none focus:border-gold resize-none"
      />
      <div className="flex items-center gap-1.5 pt-1">
        <button
          type="button"
          data-testid={`text-save-${itemId}`}
          onClick={save}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.15em] py-1.5 bg-gold/20 hover:bg-gold/40 text-gold border border-gold/40 transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : ok ? <Check size={11} /> : <Check size={11} />}
          {ok ? "Guardado" : "Guardar"}
        </button>
        <button
          type="button"
          data-testid={`text-reset-${itemId}`}
          onClick={reset}
          disabled={saving}
          aria-label="Restablecer"
          title="Restablecer al original"
          className="w-8 h-8 border border-q5 text-neutral-500 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center transition-colors disabled:opacity-40"
        >
          <RotateCcw size={11} />
        </button>
      </div>
    </div>
  );
};
